import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, } from '@angular/cdk/drag-drop';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { filter, firstValueFrom, from, Observable, switchMap, take, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LearningUnitEntity } from '../../../learning-unit/learning-unit-entity';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { SubjectEntity } from '../../../subject/subject.entity';
import { LearningSectionEntity } from '../../learning-section-entity';
import { LearningSectionLayoutEntity } from '../../learning-section-layout/learning-section-layout-entity';
import {
  LearningSectionLayoutEntityService
} from '../../learning-section-layout/services/learning-section-layout.entity.service';
import { LearningSectionContainerEntityService } from '../services/learning-section-container.entity.service';
import {
  normalizeEntitiesToRelations,
  normalizeManyRelations,
  normalizeOneRelation
} from '../../../core/decorators/relation.decorator';
import { LearningSectionContainerEntity } from '../learning-section-container-entity';
import { EntityStateModel, InvalidateLoadedQueries } from '../../../core/state/entity.state';
import { LearningSectionEntityService } from '../../services/learning-section.entity.service';
import { RelationRef } from '../../../core/types/entity-types';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { messageTypes, WidgetMessageService } from '../../../core/services/widget-message.service';
import {
  FormDialogLearningSectionContainerContentComponent
} from '../form-dialog-learning-section-container-content/form-dialog-learning-section-container-content.component';
import {
  ExplanationContentEntityService
} from '../../../explanation/content/services/explanation-content.entity.service';
import { TestEntityService } from '../../../test/services/test.entity.service';
import { SequenceEntityService } from '../../../sequence/services/sequence.entity.service';
import { ExplanationEntityService } from '../../../explanation/services/explanation.entity.service';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'pixl-form-dialog-learning-section-container',
  imports: [
    CdkDropList,
    CdkDrag,
    MatButton,
    MatButtonModule,
    MatIcon,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatDialogModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-dialog-learning-section-container.component.html',
  styleUrl: './form-dialog-learning-section-container.component.scss'
})
export class FormDialogLearningSectionContainerComponent implements OnInit {
  form: FormGroup;
  learningSectionLayouts$!: Observable<LearningSectionLayoutEntity[]>;

  private store = inject(Store);
  private dialog = inject(MatDialog);
  private widgetService = inject(WidgetMessageService);
  private cdr = inject(ChangeDetectorRef);

  private learningSectionService = inject(LearningSectionEntityService);
  private learningSectionLayoutService = inject(LearningSectionLayoutEntityService);
  private learningSectionContainerService = inject(LearningSectionContainerEntityService);

  private explanationContentService = inject(ExplanationContentEntityService);
  private testService = inject(TestEntityService);
  private sequenceService = inject(SequenceEntityService);
  private explanationService = inject(ExplanationEntityService);

  public showContainerId = signal<boolean>(false);

  containerContentData: Record<string, { name: string, type: string } | undefined> = {};

  selectedLayoutOption!: LearningSectionLayoutEntity;
  columnNumber!: number;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<FormDialogLearningSectionContainerComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: Partial<LearningSectionEntity>,
  ) {
    this.form = this.fb.group({
      id: [data?.id || null],
      name: [data?.name || ''],
      title: [data?.title || ''],
      learningSectionType: [data?.learningSectionType || ''],
      showCopyright: [data?.showCopyright || false],
      version: [data?.version || 1],
      consumer: [normalizeOneRelation<ConsumerEntity>(data?.consumer as RelationRef<ConsumerEntity>[])],
      subjects: [normalizeManyRelations<SubjectEntity>(data?.subjects as RelationRef<SubjectEntity>[])],
      learningUnits: [normalizeManyRelations<LearningUnitEntity>(data?.learningUnits as RelationRef<LearningUnitEntity>[])],
      learningSectionLayout: [normalizeOneRelation<LearningSectionLayoutEntity>(data?.learningSectionLayout as RelationRef<LearningSectionLayoutEntity>[])],
      learningSectionContainers: [normalizeManyRelations<LearningSectionContainerEntity>(data?.learningSectionContainers as RelationRef<LearningSectionContainerEntity>[])],
    });
  }

  ngOnInit() {
    const learningSectionLayoutQuery = { sort: 'name' };

    this.learningSectionLayouts$ = from(
      this.learningSectionLayoutService.ensureLoaded(learningSectionLayoutQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(
          this.learningSectionLayoutService.allByQuery(learningSectionLayoutQuery)
        )
      ),
      tap(layouts => {
        const control = this.form.get('learningSectionLayout');

        // Set default layout ID if missing
        if (layouts.length && !control?.value) {
          control?.setValue(layouts[0].id);
        }

        // Resolve selectedLayoutOption from form value
        const selectedId = control?.value;
        const selectedLayout = layouts.find(l => l.id === selectedId);

        if (selectedLayout) {
          this.selectedLayoutOption = selectedLayout;
          this.setLayout(selectedLayout);
        }
      })
    );

    this.preloadContainersWithContent().catch(err => console.error(err));
  }

  get learningSectionContainers(): LearningSectionContainerEntity[] {
    const selectedRefs = this.form.get('learningSectionContainers')?.value ?? [];
    const containerState = this.store.selectSnapshot(
      this.learningSectionContainerService.stateClass
    ) as EntityStateModel<LearningSectionContainerEntity>;

    return (selectedRefs as (string | RelationRef<LearningSectionContainerEntity>)[])
      .map(ref => {
        const id = typeof ref === 'string' ? ref : ref.id;
        return containerState.entities[id];
      })
      .filter((c): c is LearningSectionContainerEntity => !!c);
  }

  private async preloadContainersWithContent() {
    const selectedRefs = this.form.get('learningSectionContainers')?.value as
      | string[] |
      RelationRef<LearningSectionContainerEntity>[];

    if (!selectedRefs?.length) return;

    // 1. Get initial snapshots once to avoid repeated overhead
    const containerState = this.store.selectSnapshot(this.learningSectionContainerService.stateClass) as EntityStateModel<LearningSectionContainerEntity>;

    // 2. Map all containers to a list of Promises to run them in parallel
    const loadPromises = selectedRefs.map(async (ref) => {
      const id = typeof ref === 'string' ? ref : ref.id;
      let container = containerState.entities[id];

      if (!container) return;

      // Ensure content relation is loaded
      if (!container.content || !('id' in container.content)) {
        await this.learningSectionContainerService.ensureSingleLoaded(id, { include: 'content' });
        // Refresh local container ref after loading
        container = (this.store.selectSnapshot(this.learningSectionContainerService.stateClass) as EntityStateModel<LearningSectionContainerEntity>).entities[id];
      }

      const contentRef = container?.content;
      if (!contentRef || !('id' in contentRef && 'type' in contentRef)) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serviceMap: Record<string, any> = {
        'explanation-contents': this.explanationContentService,
        'tests': this.testService,
        'sequences': this.sequenceService,
        'explanations': this.explanationService
      };

      const service = serviceMap[contentRef.type];
      if (service) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const contentState = this.store.selectSnapshot(service.stateClass) as EntityStateModel<any>;
        if (!contentState.entities[contentRef.id]) {
          await service.ensureSingleLoaded(contentRef.id, {});
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const finalState = this.store.selectSnapshot(service.stateClass) as EntityStateModel<any>;
        const loadedEntity = finalState.entities[contentRef.id];

        // Update local tracking object
        this.containerContentData[id] = {
          name: loadedEntity?.name ?? '',
          type: contentRef.type
        };
      }
    });

    // 3. Wait for all requests to finish in parallel
    await Promise.all(loadPromises);

    // 4. Update UI once
    this.cdr.markForCheck();
  }

  async addContainer() {
    const learningSectionId = this.form.get('id')?.value;
    if (!learningSectionId) return;

    await this.learningSectionService.ensureSingleLoaded(learningSectionId);

    const state = this.store.selectSnapshot(
      this.learningSectionService.stateClass
    ) as EntityStateModel<LearningSectionEntity>;

    const learningSection = state.entities[learningSectionId];
    if (!learningSection) return;

    const newContainer: Partial<LearningSectionContainerEntity> = {
      title: 'Container',
      learningSections: normalizeEntitiesToRelations(
        [learningSection],
        'learning-sections'
      ),
      fullWidth: false,
      version: 1,
    };

    const createdContainer = await this.learningSectionContainerService.create(newContainer);

    this.learningSectionContainers.push(createdContainer);

    // Update form control with new container ID
    const currentIds: string[] = this.form.get('learningSectionContainers')?.value ?? [];
    this.form.get('learningSectionContainers')?.setValue([...currentIds, createdContainer.id]);
    await this.updateLearningSection();
  }

  async removeContainer(container: LearningSectionContainerEntity) {
    if (!container?.id) {
      console.error('ID is undefined!', container);
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(container.title),
            headerText: 'Container löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.learningSectionContainerService.delete(container.id));
    this.store.dispatch(new InvalidateLoadedQueries());

    const currentContainers = this.form.get('learningSectionContainers')?.value ?? [];

    // Remove the deleted container by ID
    const updatedContainers = (currentContainers as (string | RelationRef<LearningSectionContainerEntity>)[])
      .filter(c => (typeof c === 'string' ? c : c.id) !== container.id);

    this.form.get('learningSectionContainers')?.setValue(updatedContainers);

    await this.updateLearningSection();

    this.showMessage('Container erfolgreich gelöscht.');
  }

  async drop(event: CdkDragDrop<LearningSectionContainerEntity[]>) {
    const draggedContainer = event.item.data;
    const previousIndex = this.learningSectionContainers.findIndex(c => c.id === draggedContainer.id);
    const currentIndex = event.currentIndex;

    if (previousIndex === currentIndex) return;

    // Reorder the UI array
    const updatedContainers = [...this.learningSectionContainers];
    moveItemInArray(updatedContainers, previousIndex, currentIndex);

    // Update the form control with the new order as RelationRefs
    const normalizedContainers = updatedContainers.map(c => ({
      id: c.id!,
      type: 'learning-section-containers'
    }));

    this.form.get('learningSectionContainers')?.setValue(normalizedContainers);
    await this.updateLearningSection();
  }

  async onChangeLayout(selectedLayout: LearningSectionLayoutEntity) {
    if (!selectedLayout?.id) return;
    this.form.get('learningSectionLayout')?.setValue(selectedLayout.id);
    await this.updateLearningSection();
  }

  private async updateLearningSection(): Promise<void> {
    if (!this.form.valid) return;

    const formValue = this.form.value;

    const updatedLearningSection = Object.assign(
      new LearningSectionEntity(),
      { id: this.data?.id },
      formValue,
      {
        consumer: formValue.consumer ? { type: 'consumers', id: formValue.consumer } : null,
        subjects: (formValue.subjects as string[])?.length ?
          (formValue.subjects as string[]).map(id => ({ id, type: 'subjects' })) :
          [],
        learningUnits: (formValue.learningUnits as string[])?.length ?
          (formValue.learningUnits as string[]).map(id => ({ id, type: 'learning-units' })) :
          [],
        learningSectionLayout: formValue.learningSectionLayout ?
          { type: 'learning-section-layouts', id: formValue.learningSectionLayout } :
          null,
        learningSectionContainers: (formValue.learningSectionContainers as (string | RelationRef<LearningSectionContainerEntity>)[])?.length ?
          (formValue.learningSectionContainers as (string | RelationRef<LearningSectionContainerEntity>)[])
            .map(c => typeof c === 'string' ?
              { id: c, type: 'learning-section-containers' } :
              c) :
          [],
      }
    );

    await firstValueFrom(this.learningSectionService.update(updatedLearningSection));
    this.store.dispatch(new InvalidateLoadedQueries());
  }

  private setLayout(selectedLayout: LearningSectionLayoutEntity) {
    this.selectedLayoutOption = selectedLayout;
    this.columnNumber = selectedLayout.columns;
  }

  get gridTemplateColumns(): string {
    const layout = this.selectedLayoutOption;
    if (!layout || layout.columns === 1 || Number(layout.percentageSplit) === 100) {
      return '100%';
    }

    switch (Number(layout.percentageSplit)) {
      case 25: return '25% 75%';
      case 50: return '50% 50%';
      case 75: return '75% 25%';
      case 80: return '80% 20%';
      case 90: return '90% 10%';
      default: return '50% 50%'; // fallback for unknown values with 2 columns
    }
  }

  editLearningSectionSingleContainer(container: LearningSectionContainerEntity) {
    if (!container?.id) return;

    const containerState = this.store.selectSnapshot(
      this.learningSectionContainerService.stateClass
    ) as EntityStateModel<LearningSectionContainerEntity>;

    const hydratedContainer = containerState.entities[container.id];
    if (!hydratedContainer) return;

    const dialogRef = this.dialog.open(
      FormDialogLearningSectionContainerContentComponent,
      {
        width: '1200px',
        maxWidth: 'none',
        data: hydratedContainer
      }
    );

    dialogRef.afterClosed()
      .pipe(
        // Only proceed if a value was returned (not on cancel)
        filter((updated): updated is LearningSectionContainerEntity => !!updated),
        take(1) // Clean up subscription
      )
      .subscribe(() => {
        // Because the child already called .update(),
        // the store is likely updated, but we need to re-run the
        // name/type resolution for the UI labels.
        this.preloadContainersWithContent();
      });
  }

  getPreviewLink(): string | null {
    const id = this.form.get('id')?.value;
    if (!id) return null; // handle missing ID
    const learningSectionPreviewUrl = '/learning-section-preview/' + id;
    return environment.learningSectionPreviewHost + learningSectionPreviewUrl + '?token=' + environment.learningSectionPreviewToken;
  }

  private showMessage(message: string, messageTye: messageTypes = 'mtSuccess'): void {
    this.widgetService.setMessage({
      message,
      messageTye
    });
  }

  copyContainerIdToClipboard(containerId: string | undefined): void {
    if (!containerId) return;

    navigator.clipboard.writeText(containerId)
      .then(() => {
        this.showMessage('Container-ID in Zwischenablage kopiert');
      })
      .catch(() => {
        this.showMessage('Fehler beim Kopieren der Container-ID', 'mtError');
      });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  toggleContainerId(): void {
    this.showContainerId.update(value => !value);
  }
}
